import fe_config from "constants/Configs";
import Constants from "constants/index";
import { Component, lazy, Suspense } from "react";
import { connect } from "react-redux";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import login from "containers/login";
import SsoLogin from "containers/login/SsoLogin";
import get from "lodash/get";
import ForgotPassword from "containers/forgetPassword";
import resetPassword from "containers/resetPassword";
import TokenManager from "utils/TokenManager";
import ConfirmationBox from "components/ConfirmationBox";
import ErrorBoundary from "components/ErrorBoundry";
import { trackEvent } from "utils/loggly-tracker";
import { removeEmptyValueFromObject } from "utils";
import { loadMomentLocale } from "utils/dates";
import { getLocaleJson } from "actions/global";
import { initializeI18 } from "utils/localization/config";
import { isWileyClientFECheck, enableSAP_DPA } from "handlers/features";
import debounce from "lodash/debounce";
import { logout } from "handlers/setters";
import { isUserPresetAndAuthenticated } from "containers/login/utils";
import { ToastContainer } from "react-toastify";
import SsoSessionExpiredModal from "components/SsoSessionExpiredModal";
import ProtectedRoute from "./ProtectedRoute";

const NavWrapper = lazy(() => import("./navWrapper"));
const CustomerLoginRedirect = lazy(
  () => import("containers/customer-login/login")
);
const VendorLoginRedirect = lazy(
  () => import("containers/vendors/invoicing-portal/login")
);
const ProtectedNavWrapper = (props) => (
  <Suspense fallback={null}>
    <NavWrapper {...props} />
  </Suspense>
);
const ApplicationAnnouncementBanner = lazy(
  () =>
    import(
      "components/ApplicationAnnouncementBanner/ApplicationAnnouncementBanner"
    )
);
const GuestCheckout = lazy(
  () => import("containers/guest_checkout/GuestCheckout")
);

const openRoutes = [
  "/customer/rel/",
  "receivables",
  "payables",
  "/settings/profile",
  "/vendors/rel/",
]; // routes which are open for customer & vendor

const INACTIVE_TIMEOUT_IN_MIN = 30; // 30 mins
export const LAST_ACTIVITY_TIME = "lastActivityTime";
export const LOCAL_INACTIVE_TIMEOUT_IN_MIN = "localInactivityTimeoutIn";

const saveLastActivityTimeInLclStrg = (time) =>
  localStorage.setItem(LAST_ACTIVITY_TIME, time);

const getLastActivityTimeFrmLclStrg = () => {
  const timeout = localStorage.getItem(LAST_ACTIVITY_TIME);
  if (timeout) {
    return Number(timeout);
  }
  return null;
};

let isAfterWarnEvent = false;
let API_IN_PROGRESS = false;
const refreshTokenTimeout = 300000; // 5 mins
class Root extends Component {
  constructor(props) {
    super(props);
    // Low-frequency events - attach directly
    this.events = ["load", "click", "keypress"];
    // High-frequency events - debounce to prevent performance issues
    this.debouncedEvents = ["scroll", "mousemove", "mousedown"];
    this.setRefreshTimeout = this.setRefreshTimeout.bind(this);
    this.storageChange = this.storageChange.bind(this);
    this.lastUserAction = null;
    this.minTimeout = null;
    this.lastEventTime = null;
    this.warnTimeout = null;
    this.isTokenRefreshedOnce = false;
    this.lastStorageUpdateTime = null; // Track last time we updated localStorage
    this.uxActions = null;
    this.getNewToken = null;
    // this.inactivityStartTime = null;
    window.addEventListener("storage", this.storageChange, false);
    this.state = {
      path: "",
      announcement: null,
    };

    this.getInactiveTimeoutMs = () => {
      const baseTimeoutInMin =
        fe_config.MODE === "DEV" &&
        localStorage.getItem(LOCAL_INACTIVE_TIMEOUT_IN_MIN)
          ? Number(localStorage.getItem(LOCAL_INACTIVE_TIMEOUT_IN_MIN))
          : INACTIVE_TIMEOUT_IN_MIN;

      return baseTimeoutInMin * 60 * 1000; // Convert minutes to milliseconds
    };
    // Add window focus listener
    window.addEventListener("focus", this.handleWindowFocus);

    // Debounce the high-frequency events (scroll, mousemove, mousedown)
    this.debouncedActivityHandler = debounce(this.setRefreshTimeout, 800);

    // Attach low-frequency event listeners directly
    this.events.forEach((d) =>
      window.addEventListener(d, this.setRefreshTimeout)
    );

    // Attach high-frequency event listeners with debouncing
    this.debouncedEvents.forEach((d) =>
      window.addEventListener(d, this.debouncedActivityHandler)
    );
  }

  UNSAFE_componentWillMount() {
    this.setState({
      path: window.location.pathname,
    });
  }

  componentDidMount() {
    loadMomentLocale(localStorage.getItem("locale") || "en").catch((error) => {
      console.error("Failed to load moment locale:", error);
    });
    isUserPresetAndAuthenticated()
      .then(async (isAuthenticated) => {
        if (isAuthenticated) {
          const [
            { getUserSettings, refreshClients },
            { getConfigSettings, getAgingBucket },
            {
              getClientData,
              getAccountTypes,
              getPayrollSettings,
              getPaymentAccountType,
            },
            { getUsersList },
            { getConfigVariables },
            { refresh_tag },
            { getDisputeCodes },
            { createScriptTag },
            { getInvoicingFields },
            { fetchEmailTemplateList },
            { fetchWorkflowSteps, setWorkflowData },
            { refetchCustomerInvoices },
            uxActions,
            { getActiveUserManager },
            { default: getNewToken },
          ] = await Promise.all([
            import("actions/user"),
            import("containers/settings/action"),
            import("actions/client"),
            import("containers/dashboard/TaskManagement/actions"),
            import("containers/support/api"),
            import("components/Tag"),
            import("containers/settings/company/dispute_reason/actions"),
            import("utils/createScriptTag"),
            import("containers/settings/payables/actions"),
            import("containers/settings/receivables/ARworkflow/actions"),
            import("containers/customers/listnew/actions"),
            import("containers/customers/details/actions"),
            import("handlers/ux"),
            import("utils/oidcUserManager"),
            import("utils/token"),
          ]);
          this.uxActions = uxActions;
          this.getNewToken = getNewToken;

          // Use delegated click listener for better performance
          document.addEventListener("click", this.logClickEvents, true);

          if (!get(this.props, "clients", []).length) {
            getUserSettings(refreshClients);
          }
          this.setRefreshTimeout();
          getConfigSettings();
          this.props.dispatch(getConfigVariables());
          this.props.dispatch(getClientData());
          this.props.dispatch(getUsersList());
          if (uxActions.uxempty()) {
            uxActions.ux_refresh();
          }
          refresh_tag();
          getDisputeCodes();
          this.props.dispatch(getPayrollSettings());
          getPaymentAccountType().then(() => {
            createScriptTag();
            if (TokenManager.isCustomer() && enableSAP_DPA()) {
              [
                "ar_invoice",
                "ar_closed",
                "customer_credit",
                "ar_payment_history",
              ].forEach((invoiceType) => {
                refetchCustomerInvoices(invoiceType);
              });
            }
          });
          if (
            !TokenManager.isCustomer() &&
            !TokenManager.getIsAP_REP() &&
            !TokenManager.isReadOnlyARUser()
          ) {
            this.props.dispatch(fetchEmailTemplateList());
            getInvoicingFields();
            if (!TokenManager.isVendor()) {
              getAccountTypes();
            }
          }
          if (
            !TokenManager.isCustomer() &&
            !TokenManager.isVendor() &&
            !TokenManager.getIsAP_REP()
          ) {
            // fetch sequence steps
            fetchWorkflowSteps().then((res) =>
              this.props.dispatch(
                setWorkflowData({ workflows: res?.data?.result })
              )
            );
            getAgingBucket({ bucket_type: "ar" });
            getAgingBucket({ bucket_type: "ap" });
          }
          uxActions.check_ux_properties();

          // For Ping SSO: drive TokenManager sync and syncUserSettings
          // via oidc-client-ts userLoaded event instead of warnTimeout cycle
          if (TokenManager.isPingAuthorizationEnabled()) {
            this.onPingUserLoaded = (user) => {
              // Only sync expiry — username/userid are set at login and on context
              // switches via auth/verify; OIDC profile claims (sub, preferred_username)
              // differ from the app's internal values and must not overwrite them.
              TokenManager.setExpiredAt(user.expires_at * 1000);
              // Pass false to skip ux_refresh() — OIDC renewal does not change user
              // context, so re-fetching UX settings is unnecessary and would dispatch
              // SET_UX causing page components to re-render and re-fetch their data.
              uxActions.syncUserSettings(false);
            };
            this.pingUserManager = getActiveUserManager();
            this.pingUserManager.events.addUserLoaded(this.onPingUserLoaded);
          }
        }
        // ...existing code...
      })
      .catch((error) => {
        console.error(
          "[index routes]Error fetching user authentication: ",
          error
        );
      });

    this.props
      .getLocaleJson(localStorage.getItem("locale") || "en")
      .then((translations) => {
        initializeI18({
          [localStorage.getItem("locale") || "en"]: { translations },
        });
      });
  }

  handleWindowFocus = async () => {
    // for testing on Dev we have used localstorage time in min

    const INACTIVE_TIMEOUT = this.getInactiveTimeoutMs(); // Convert minutes to milliseconds

    const currentTime = Date.now();

    const lastActivityTimestamp = getLastActivityTimeFrmLclStrg();

    // Check if the last activity timestamp is older than the inactive timeout
    if (
      lastActivityTimestamp &&
      currentTime - lastActivityTimestamp >= INACTIVE_TIMEOUT
    ) {
      // User has been inactive - check if they have an active session to log out
      if (TokenManager.isPingAuthorizationEnabled()) {
        // Use the oidc-client-ts manager to check the current user session
        const { getActiveUserManager } = await import("utils/oidcUserManager");
        const user = await getActiveUserManager().getUser();
        // Only log out if user has a valid, non-expired session
        //TODO: Check if this logic is correct, as we are logging out user if token is not expired. Need to check with product team if this is the expected behavior.
        if (user && !user.expired) {
          const now = Math.floor(Date.now() / 1000);
          const tokenExpiry = user.expires_at;
          // If token is not expired, user is still logged in - log them out due to inactivity
          if (now < tokenExpiry) {
            API_IN_PROGRESS = true;
            // forceLogout=false: still sync user settings before logging out,
            // logout() caps the wait so the modal can't be blocked indefinitely
            logout();
            localStorage.removeItem(LAST_ACTIVITY_TIME);
            return;
          }
        }
        // User session doesn't exist or already expired — no action needed
        return;
      } else if (!TokenManager.get()) {
        // No token exists for non-Ping auth - user already logged out, no action needed
        return;
      }

      // Non-Ping auth with valid token - perform logout due to inactivity
      API_IN_PROGRESS = true;
      logout();

      // remove last activity time from local storage
      localStorage.removeItem(LAST_ACTIVITY_TIME);
    } else {
      saveLastActivityTimeInLclStrg(currentTime); // Update the last activity timestamp
      this.lastStorageUpdateTime = currentTime; // Track this update
    }
  };

  // get new token when timeout
  refreshToken = (flag) => {
    if (TokenManager.isPingAuthorizationEnabled()) {
      // Ping SSO tokens are managed via the OIDC redirect flow; no BE-based refresh needed
      return;
    }
    if (isAfterWarnEvent && !this.props.refresh_header) {
      // if entity or client is changed then don't call this function
      if (API_IN_PROGRESS) {
        return;
      }
      if (!this.getNewToken || !this.uxActions) {
        console.error(
          "Skipping token refresh because refresh dependencies are not initialized"
        );
        return;
      }
      isAfterWarnEvent = false;
      API_IN_PROGRESS = true;
      const clientId = TokenManager.getClientId();
      const corpId = TokenManager.getCorporateEntityId();
      const hierarchical_corporate_entity_list_context = localStorage.getItem(
        "hierarchical_corporate_entity_list_context"
      );
      const headers = {
        client_id_context: clientId,
      };

      if (TokenManager.getClientCustomerRelId()) {
        headers["client_customer_rel_id_context"] =
          TokenManager.getClientCustomerRelId();
      }
      if (TokenManager.getClientSupplierRelId()) {
        headers["client_supplier_rel_id_context"] =
          TokenManager.getClientSupplierRelId();
      }
      if (TokenManager.getARrepContext()) {
        headers["client_customer_ar_rep_list"] = TokenManager.getARrepContext();
      }
      if (
        hierarchical_corporate_entity_list_context &&
        hierarchical_corporate_entity_list_context !== "null"
      ) {
        headers["hierarchical_corporate_entity_list_context"] =
          hierarchical_corporate_entity_list_context;
      }
      if (corpId) {
        headers["corporate_entity_id_context"] = corpId;
      }

      this.getNewToken({ headers: removeEmptyValueFromObject(headers) }, () => {
        API_IN_PROGRESS = false;
        isAfterWarnEvent = false;
        if (!this.isTokenRefreshedOnce) {
          this.clearSetTimeout();
        }
        // Pass false to skip ux_refresh() — token renewal does not change user context,
        // so re-fetching UX settings would unnecessarily dispatch SET_UX and cause
        // page components to re-render and re-fetch their data.
        this.uxActions.syncUserSettings(false);
        this.setRefreshTimeout(flag);
      });
    }
  };

  validateTokenTimeout = () => {
    this.lastEventTime = null;
    isAfterWarnEvent = true;

    const isBrowserInactive = document.visibilityState === "hidden"; // check if the browser is inactive

    if (!this.isTokenRefreshedOnce) {
      if (isBrowserInactive) {
        // Update shared activity timestamp in localStorage
        const now = Date.now();
        saveLastActivityTimeInLclStrg(now);
        this.lastStorageUpdateTime = now; // Track this update
        this.setRefreshTimeout(true);
      }
    }
  };

  setRefreshTimeout = (flag) => {
    API_IN_PROGRESS = false;
    isAfterWarnEvent = false;
    const currentTime = Math.round(new Date().getTime());

    //updating last activity time in localStorage only if the tab is in focus and not updating it more than once per minute
    // Done before auth-type gate so Ping inactivity tracking still works
    if (document.hasFocus()) {
      // Throttle localStorage updates to once per minute (reduces overhead)
      // Only update if 60 seconds have passed since last update or if this is the first update
      const STORAGE_UPDATE_THROTTLE = 60000; // 1 minute
      if (
        !this.lastStorageUpdateTime ||
        currentTime - this.lastStorageUpdateTime >= STORAGE_UPDATE_THROTTLE
      ) {
        saveLastActivityTimeInLclStrg(currentTime);
        this.lastStorageUpdateTime = currentTime;
      }
    }

    // Ping SSO: only activity tracking above is needed; token renewal is via oidc-client-ts
    if (TokenManager.isPingAuthorizationEnabled()) return;

    const tokenExpiryTime = TokenManager.getExpiredAt();
    if (!tokenExpiryTime) {
      return;
    }
    // Authenticated startup loads these dependencies asynchronously. Wait for
    // that import to finish; componentDidMount schedules refreshes again after
    // assigning the dependencies.
    if (!this.getNewToken || !this.uxActions) {
      return;
    }
    this.lastEventTime = currentTime;

    /*
     * refreshTokenTime: used to reset the timeout only for testing purpose
     * basically for tester to not wait for default set time to refresh token
     */

    const reduceRefreshTime =
      fe_config.MODE === "DEV" && localStorage.getItem("refreshTokenTime")
        ? Number(localStorage.getItem("refreshTokenTime")) * 60 * 1000
        : 0; // for testing in local we can reduce refresh token timeout

    const REFRESH_TOKEN_TIME = reduceRefreshTime
      ? reduceRefreshTime
      : refreshTokenTimeout; // refresh before 5 mins

    const warnTime = tokenExpiryTime - currentTime - REFRESH_TOKEN_TIME;

    if (!this.warnTimeout) {
      this.warnTimeout = setTimeout(() => {
        isAfterWarnEvent = true;
        this.refreshToken(true);
        this.isTokenRefreshedOnce = true;
      }, warnTime);
    }

    if (typeof flag === "boolean") {
      return;
    }

    if (this.minTimeout) clearTimeout(this.minTimeout);

    this.isTokenRefreshedOnce = false;

    this.minTimeout = setTimeout(() => {
      this.validateTokenTimeout();
    }, refreshTokenTimeout);
  };

  logClickEvents = (e) => {
    const dataAttributeValue = get(e, "target.dataset.tlabel", "");
    const eventObj = {
      clicked_element:
        dataAttributeValue ||
        e.target.outerText ||
        e.target.textContent ||
        e.target.outerHTML,
    };
    const existingElement =
      localStorage.getItem("lastAction") &&
      JSON.parse(localStorage.getItem("lastAction"));

    if (
      eventObj.clicked_element !==
      (existingElement && existingElement.clicked_element)
    ) {
      const message = `Clicked on ${eventObj.clicked_element}`;
      trackEvent(message, "click");
    }
    localStorage.setItem("lastAction", JSON.stringify(eventObj));
  };

  storageChange(event) {
    if (event.key === "REFRESHED") {
      window.location.reload(true);
    }
    if (event.key === "loggedout") {
      // Ping/SSO users get the session-expired modal via ssoTabSync's own
      // cross-tab broadcast; skip the legacy hard-redirect for them only
      if (TokenManager.isPingAuthorizationEnabled()) {
        return;
      }
      window.sessionStorage.setItem("oldUrl", window.location.href);
      window.sessionStorage.setItem("oldUser", event.newValue);
      window.location.pathname !== "/login" && window.open("/login", "_self");
    }
    if (event.key === "loggedin") {
      if (event.newValue === window.sessionStorage.getItem("oldUser")) {
        const URL = window.sessionStorage.getItem("oldUrl");
        if (URL) {
          window.open(URL, "_self");
        }
      } else {
        window.close();
      }
    }

    // below will make sure that last activity time is syncronized across tabs
    if (event.key === LAST_ACTIVITY_TIME) {
      const lastActivityTime = Number(event.newValue);
      const currentTime = Date.now();
      const INACTIVE_TIMEOUT = this.getInactiveTimeoutMs(); // Convert minutes to milliseconds

      // Reset inactivity tracking if activity is detected in another tab
      if (
        lastActivityTime &&
        currentTime - lastActivityTime <= INACTIVE_TIMEOUT
      ) {
        this.setRefreshTimeout(true);
      }
    }
  }

  clearSetTimeout() {
    if (this.warnTimeout) {
      clearTimeout(this.warnTimeout);
      this.warnTimeout = null;
    }
    if (this.minTimeout) {
      clearTimeout(this.minTimeout);
      this.minTimeout = null;
    }
  }

  componentWillUnmount() {
    this.destroy();
  }

  destroy() {
    this.clearSetTimeout();

    // Remove Ping userLoaded listener if registered
    if (this.onPingUserLoaded) {
      this.pingUserManager?.events.removeUserLoaded(this.onPingUserLoaded);
      this.onPingUserLoaded = null;
      this.pingUserManager = null;
    }

    // Remove low-frequency event listeners
    for (const eventName of this.events) {
      window.removeEventListener(eventName, this.setRefreshTimeout);
    }

    // Remove high-frequency debounced event listeners
    for (const eventName of this.debouncedEvents) {
      window.removeEventListener(eventName, this.debouncedActivityHandler);
    }

    // Cancel any pending debounced invocations to prevent execution after unmount
    if (this.debouncedActivityHandler && this.debouncedActivityHandler.cancel) {
      this.debouncedActivityHandler.cancel();
    }

    window.removeEventListener("focus", this.handleWindowFocus);
    window.removeEventListener("storage", this.storageChange);
    // Remove delegated click listener
    document.removeEventListener("click", this.logClickEvents, true);
  }

  render() {
    const openRouteAvailable = openRoutes.some((d) =>
      this.state.path.includes(d)
    )
      ? this.state.path
      : "";
    const isVendorRouteAvailable = openRouteAvailable.includes("vendors");

    // Check if URL has a token parameter (indicates email-link token-based login)
    const hasTokenParam = window.location.search.includes("token=");

    // url contains login or signup or forgot-password
    return (
      <ErrorBoundary>
        <ToastContainer stacked={isWileyClientFECheck()} />
        <ConfirmationBox />
        <SsoSessionExpiredModal />
        <Router>
          <Switch>
            <Route exact path="/login" component={login} />
            <Route exact path="/sso-login" component={SsoLogin} />
            <Route
              exact
              path="/forgot-password"
              render={(props) => (
                <ForgotPassword operation="forgot-password" {...props} />
              )}
            />
            <Route
              exact
              path="/user-signup"
              render={(props) => (
                <ForgotPassword text="user-signup" {...props} />
              )}
            />
            <Route
              exact
              path="/token-expired"
              render={(props) => (
                <ForgotPassword tokenExpired={true} {...props} />
              )}
            />
            <Route exact path="/reset-password" component={resetPassword} />
            {Object.values(Constants.GuestLoginRouteMap).map(
              (guestRoute, index) => (
                <Route
                  key={index}
                  exact
                  path={guestRoute.route}
                  component={(compProps) => (
                    <>
                      <Suspense fallback={null}>
                        <ApplicationAnnouncementBanner
                          announcement={this.state.announcement}
                          onDismiss={() =>
                            this.setState({ announcement: null })
                          }
                        />
                        <GuestCheckout
                          {...compProps}
                          client_id={guestRoute[fe_config.MODE]}
                          logo_url={guestRoute.logo_url}
                        />
                      </Suspense>
                    </>
                  )}
                />
              )
            )}
            {!TokenManager.get() &&
              (!TokenManager.isPingAuthorizationEnabled() || hasTokenParam) &&
              openRouteAvailable && (
                <Route
                  exact
                  path={openRouteAvailable}
                  render={(routeProps) => (
                    <Suspense fallback={null}>
                      {isVendorRouteAvailable ? (
                        <VendorLoginRedirect {...routeProps} />
                      ) : (
                        <CustomerLoginRedirect {...routeProps} />
                      )}
                    </Suspense>
                  )}
                />
              )}
            <ProtectedRoute path="/" component={ProtectedNavWrapper} />
          </Switch>
        </Router>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = (state) => ({
  refresh_header: state.header.refresh_header,
  clients: state.user.clients,
  userSettings: state.user.userSettings,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  getLocaleJson: (...args) => dispatch(getLocaleJson(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Root);
